import { useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import {
  useNetzplanImportStore,
  type ObjectDecision,
  type ObjectSource,
} from '@/store/useNetzplanImportStore';
import type { DecisionAction } from '@/pages/Import/logic/types';
import { CategoryBadge } from './CategoryBadge';
import { StepFooter } from './StepFooter';

const INPUT =
  'w-full px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';
const ROW = 'grid grid-cols-[minmax(0,1.4fr)_88px_96px_minmax(0,1.8fr)] gap-3 px-3 py-2 items-start';

export function ObjectStep() {
  const { t } = useTranslation('import');
  const { objectSources, objectDecisions, customerSources, customerDecisions, setObjectDecision, live } =
    useNetzplanImportStore();
  const [onlyOpen, setOnlyOpen] = useState(true);

  const custName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of customerSources) {
      const d = customerDecisions[s.id];
      map.set(s.id, d?.action === 'link' ? (live.customers.find((c) => c.id === d.linkedCustomerId)?.name ?? s.rawName) : s.rawName);
    }
    return map;
  }, [customerSources, customerDecisions, live.customers]);

  // Objects only make sense when their customer is not skipped.
  const activeSources = useMemo(
    () => objectSources.filter((o) => customerDecisions[o.customerSourceId]?.action !== 'skip'),
    [objectSources, customerDecisions],
  );

  const rows = useMemo(
    () => (onlyOpen ? activeSources.filter((s) => s.category !== 'MATCH') : activeSources),
    [activeSources, onlyOpen],
  );

  const options: SearchSelectOption[] = useMemo(
    () => live.objects.map((o) => ({ id: o.id, label: o.name, description: o.street })),
    [live.objects],
  );

  const counts = useMemo(() => {
    let create = 0;
    let link = 0;
    let skip = 0;
    for (const s of activeSources) {
      const a = objectDecisions[s.key]?.action;
      if (a === 'create') create += 1;
      else if (a === 'link') link += 1;
      else if (a === 'skip') skip += 1;
    }
    return { create, link, skip };
  }, [activeSources, objectDecisions]);

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-[13px] text-slate-600">{t('objects.hint')}</p>
          <label className="flex items-center gap-2 text-[12px] text-slate-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            {t('objects.onlyOpen')}
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className={`${ROW} bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400`}>
            <span>{t('objects.colSource')}</span>
            <span>{t('customers.colCategory')}</span>
            <span>{t('customers.colAction')}</span>
            <span>{t('customers.colTarget')}</span>
          </div>

          <div className="max-h-[52vh] overflow-y-auto divide-y divide-slate-100">
            {rows.map((src) => (
              <ObjectRow
                key={src.key}
                src={src}
                customerName={custName.get(src.customerSourceId) ?? ''}
                decision={objectDecisions[src.key]}
                options={options}
                onChange={(patch) => setObjectDecision(src.key, patch)}
              />
            ))}
            {rows.length === 0 && (
              <p className="px-3 py-6 text-center text-[12px] text-slate-500">{t('objects.empty')}</p>
            )}
          </div>
        </div>

        <StepFooter
          back="customers"
          next="tasks"
          summary={t('objects.summary', { create: counts.create, link: counts.link, skip: counts.skip })}
        />
      </div>
    </Card>
  );
}

function ObjectRow({
  src,
  customerName,
  decision,
  options,
  onChange,
}: {
  src: ObjectSource;
  customerName: string;
  decision: ObjectDecision | undefined;
  options: SearchSelectOption[];
  onChange: (patch: Partial<ObjectDecision>) => void;
}) {
  const { t } = useTranslation('import');
  if (!decision) return null;

  const suggested: SearchSelectOption[] = src.suggestion
    ? [{ id: src.suggestion.id, label: src.suggestion.name, description: src.suggestion.street }]
    : [];
  const missingPlz = decision.action === 'create' && !decision.plz.trim();

  return (
    <div className={ROW}>
      <div className="min-w-0">
        <div className="text-[13px] text-slate-900 truncate">{src.objName}</div>
        <div className="text-[11px] text-slate-500 truncate">{customerName}</div>
      </div>

      <div className="pt-0.5">
        <CategoryBadge category={src.category} score={src.score} />
      </div>

      <select
        value={decision.action}
        onChange={(e) => onChange({ action: e.target.value as DecisionAction })}
        className={INPUT}
      >
        <option value="link">{t('actions.link')}</option>
        <option value="create">{t('actions.create')}</option>
        <option value="skip">{t('actions.skip')}</option>
      </select>

      <div className="min-w-0">
        {decision.action === 'link' && (
          <SearchSelect
            options={options}
            suggestedOptions={suggested}
            value={decision.linkedObjectId}
            onChange={(id) => onChange({ linkedObjectId: id })}
            placeholder={t('objects.pickObject')}
          />
        )}
        {decision.action === 'create' && (
          <div className="space-y-1.5">
            <input className={INPUT} value={decision.name} onChange={(e) => onChange({ name: e.target.value })} placeholder={t('objects.name')} />
            <div className="grid grid-cols-[1.6fr_0.8fr_1.2fr] gap-1.5">
              <input className={INPUT} value={decision.street} onChange={(e) => onChange({ street: e.target.value })} placeholder={t('customers.street')} />
              <input className={`${INPUT} ${missingPlz ? 'border-amber-300' : ''}`} value={decision.plz} onChange={(e) => onChange({ plz: e.target.value })} placeholder={t('customers.plz')} />
              <input className={INPUT} value={decision.ort} onChange={(e) => onChange({ ort: e.target.value })} placeholder={t('customers.ort')} />
            </div>
            {missingPlz && <p className="text-[11px] text-amber-600">{t('objects.needAddress')}</p>}
          </div>
        )}
        {decision.action === 'skip' && (
          <span className="text-[12px] text-slate-400">{t('objects.skipHint')}</span>
        )}
      </div>
    </div>
  );
}

export default ObjectStep;
